import { ApplicantStage } from 'src/graphql/types/applicants'

/**
 * Each pipeline stage is counted on its own date column on the Applicant
 * model: PROSPECT on applicationDate, HIRED on hiredDate, and so on. Used in
 * `stage-event` mode (Overview tab + Pipeline by Source) where the resolver
 * counts applicants by the stage *event* falling in the window. Keep this map
 * in sync with the `APPLICANT_STAGE_DATE_FIELD` map in
 * `api/src/services/pipelineMetrics/`.
 */
const STAGE_TO_DATE_FILTER_KEY: Record<ApplicantStage, string> = {
  [ApplicantStage.PROSPECT]: 'applicationDate',
  [ApplicantStage.HIRED]: 'hiredDate',
  [ApplicantStage.NOT_INTERESTED]: 'notInterestedDate',
  [ApplicantStage.DISQUALIFIED]: 'disqualifiedDate',
  [ApplicantStage.TERMINATED]: 'terminatedDate',
}

/**
 * Three URL shapes, picked per caller's semantics:
 *
 * - `stage-event` (default): "count applicants whose <stage>Date falls in
 *   the window, regardless of current stage." Selects the stage-specific
 *   date column from STAGE_TO_DATE_FILTER_KEY and deliberately omits
 *   `applicantStages` from the URL. Used by Overview KPIs, Pipeline Summary,
 *   Pipeline Stages by Listing, and the Marketing tab's Pipeline by Source
 *   card.
 *
 * - `current-stage`: "count applicants whose application date falls in the
 *   window AND who are currently in <stage>." Always uses `applicationDate`
 *   for the range, and sets `applicantStages=<STAGE>` (or `STAGE-REASON`
 *   when `reason` is provided).
 *
 * - `cohort-reached`: "count applicants whose application date falls in
 *   the window AND whose <stage>Date is non-null." The lead-cohort framing
 *   used by Pipeline Outcomes after the cohort migration: it captures the
 *   ever-reached-stage set (so an applicant hired then later terminated
 *   still counts). Implemented by:
 *     - `applicationDateGte/Lte` = window
 *     - `<stage>DateGte` = window.gte (a date filter on a nullable column
 *       in Prisma excludes NULLs by definition, so any gte value picks
 *       the rows with the stage reached. Using window.gte here — rather
 *       than a 1970 epoch sentinel — makes the chip render sensibly on
 *       the Applicants page; cohort SQL's `<stage>_date >= applicationDate`
 *       guard guarantees the chosen floor doesn't narrow the cohort.)
 *     - no `applicantStages` (current stage doesn't matter; reached-status
 *       matters)
 *
 * Don't pick the mode by guessing — look at how the underlying resolver
 * counts the metric being clicked. The wrong mode silently undercounts or
 * overcounts the cohort the user lands on.
 */
export type BuildApplicantsFilterUrlMode =
  | 'stage-event'
  | 'current-stage'
  | 'cohort-reached'


export interface BuildApplicantsFilterUrlInput {
  /** Defaults to 'stage-event' to preserve the original Overview-tab behavior. */
  mode?: BuildApplicantsFilterUrlMode
  /**
   * In `stage-event` mode: selects which stage-specific date column to filter
   * on (e.g. HIRED → hiredDate). Omit for the "Total Leads" definition
   * (applicationDate, all stages).
   *
   * In `current-stage` mode: emitted as the `applicantStages` URL param so
   * the Applicants page filters to applicants currently in this stage.
   */
  stage?: ApplicantStage
  /**
   * `current-stage` mode only. Combined with `stage` as `STAGE-REASON` in
   * the `applicantStages` URL param — matches the encoding parsed by
   * `loadFiltersFromUrl.ts` (applicant-stage-filter type). Meaningful for
   * NOT_INTERESTED and DISQUALIFIED reason drill-downs in Pipeline
   * Outcomes. Ignored in `stage-event` mode.
   */
  reason?: string
  dateRange: { gte: Date; lte: Date }
  /**
   * Single-listing variant used by the per-job-listing table cells. Encoded
   * as the `jobListingId` URL param the Applicants page accepts directly.
   * Mutually exclusive with `jobListingIds` — pass one or the other.
   */
  jobListingId?: string
  /**
   * Multi-select facets sourced from the FiltersBar on the analytics page.
   * The resolvers that respect these facets apply them at the query, so the
   * deep-link must apply them on the Applicants page too — otherwise the
   * Applicants list opens on a broader cohort than the count the user
   * clicked. Empty arrays are treated the same as omitting the field.
   */
  recruiterIds?: ReadonlyArray<string>
  jobListingIds?: ReadonlyArray<string>
  leadSources?: ReadonlyArray<string>
}

/**
 * Builds the relative URL the Analytics page navigates to when a number is
 * clicked. Mirrors the URL schema parsed by
 * `web/src/pages/ApplicantsPage/loadFiltersFromUrl.ts`.
 *
 * Two subtleties worth knowing about:
 *
 * 1. **Mode dictates whether `applicantStages` is set.** In `stage-event`
 *    mode (Overview, Pipeline by Source) we deliberately omit it: setting
 *    `applicantStages=HIRED` would filter to currently-HIRED applicants and
 *    drop anyone who has since been terminated, undercounting an event
 *    count. In `current-stage` mode (Pipeline Outcomes) we deliberately
 *    *set* it because the resolver itself groups by current stage — and
 *    `applicationDate` becomes the date column regardless of `stage`.
 *
 * 2. **ISO timestamps, not date-only.** `loadFiltersFromUrl` parses date
 *    params with `new Date(string)`. A bare `YYYY-MM-DD` string is treated
 *    as UTC midnight (i.e. the *start* of that day), so any applicant
 *    whose timestamp falls later on the selected end date would be
 *    excluded — undercounting again. Full ISO timestamps preserve the
 *    exact instants the analytics resolver used.
 */
export const buildApplicantsFilterUrl = ({
  mode = 'stage-event',
  stage,
  reason,
  dateRange,
  jobListingId,
  recruiterIds,
  jobListingIds,
  leadSources,
}: BuildApplicantsFilterUrlInput): string => {
  const params = new URLSearchParams()

  // In current-stage and cohort-reached modes the primary date column is
  // ALWAYS applicationDate (the resolvers window on application date).
  // In stage-event mode we pick the per-stage date column; falling back to
  // applicationDate when no stage is provided matches the "Total Leads"
  // hero card.
  const dateFilterKey =
    mode === 'current-stage' || mode === 'cohort-reached'
      ? 'applicationDate'
      : stage
        ? STAGE_TO_DATE_FILTER_KEY[stage]
        : 'applicationDate'
  // Exempt from the "use serializeDateRangeForUrl" rule: that helper produces
  // a single combined `YYYY-MM-DD,YYYY-MM-DD` value for the Analytics page's
  // own URL state, but the Applicants page expects per-field {key}Gte/Lte
  // params with full ISO precision (see subtlety #2 in the JSDoc above).
  // `.toISOString()` is also WAF-safe — the rule was added to ban
  // `Date.toString()` output (parentheses, timezone names) that Cloudflare
  // flagged, not canonical ISO-8601 strings.
  params.set(`${dateFilterKey}Gte`, dateRange.gte.toISOString())
  params.set(`${dateFilterKey}Lte`, dateRange.lte.toISOString())

  if (mode === 'current-stage' && stage) {
    // Encoding matches loadFiltersFromUrl.ts applicant-stage-filter parsing:
    // STAGE alone, or STAGE-REASON for NI/DQ drill-down rows.
    params.set('applicantStages', reason ? `${stage}-${reason}` : stage)
  }

  if (mode === 'cohort-reached' && stage) {
    // "Ever reached <stage>" — encode as a Gte on the stage's date column
    // set to the WINDOW START. Prisma's date-range filter on a nullable
    // column excludes NULLs, so any Gte picks up only applicants who
    // reached the stage at least once. We use window.gte (instead of a
    // 1970 epoch sentinel) so the chip on the Applicants page renders
    // sensibly — "Hired Date: <window start>" reads correctly instead of
    // "Hired Date: 12/31/1969" which alarmed users. The cohort SQL has
    // `<stage>_date >= applicationDate AND applicationDate ∈ window` (or
    // its IS NOT NULL equivalent), so any reached-stage applicant in this
    // cohort necessarily has <stage>_date >= window.gte — using window.gte
    // as the floor doesn't narrow the set. Lte intentionally omitted so
    // we don't accidentally chop off downstream stage events.
    //
    // PROSPECT's date column IS applicationDate, so the cohort-reached
    // predicate would clobber the window we just wrote above. The
    // predicate is also redundant — every applicant has applicationDate
    // set by definition — so skip it for PROSPECT and let the
    // applicationDate window stand on its own.
    const stageDateKey = STAGE_TO_DATE_FILTER_KEY[stage]
    if (stageDateKey !== dateFilterKey) {
      params.set(`${stageDateKey}Gte`, dateRange.gte.toISOString())
    }
    // Reason drill-downs deliberately stay on `current-stage` mode (see
    // PipelineOutcomesTable's navigateToStage call site) because the
    // applicants page's reason filter is keyed off `applicantStages`,
    // which requires the current stage. cohort-reached + reason would
    // mean "applicants who applied in window, ever reached <stage>, AND
    // are currently in <stage> with <reason>" — a useful but stricter
    // filter that's not what this mode is for.
  }

  if (jobListingId) {
    params.set('jobListingId', jobListingId)
  }
  if (recruiterIds && recruiterIds.length > 0) {
    params.set('recruiters', recruiterIds.join(','))
  }
  if (jobListingIds && jobListingIds.length > 0) {
    params.set('jobListings', jobListingIds.join(','))
  }
  if (leadSources && leadSources.length > 0) {
    params.set('leadSources', leadSources.join(','))
  }

  return `/applicants?${params.toString()}`
}
